import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import Button from '../../../../components/ui/Button';
import { generateWpsFile, holdEveryone, releaseEveryone } from '../../../../api/Payroll/wps';
import { SifPreflightView, WpsConfirmWarning, WpsCoverageView } from '../../../../models/payroll';
import { getApiErrorMessage } from '../../../../utils/apiErrors';
import { InlineConfirm, WarningList } from './InlineConfirm';
import { PayrollTableLink } from './PayrollTableLink';
import { useWpsConfirm } from './useWpsConfirm';
import { useWpsLabels } from './wpsLabels';

interface GeneratePanelProps {
  year: number;
  month: number;
  monthName: string;
  preflight: SifPreflightView | null;
  coverage: WpsCoverageView | null;
  holdBackEnabled: boolean;
  /** The month's checks are on their way, so a missing answer is not a failed one yet. */
  loading: boolean;
  /** Warn only: PeoplePanel carries the named warning, and these people do not hold the month open. */
  noPayrollWarnOnly: boolean;
  onShowHeld: () => void;
  onChanged: () => void;
  onGenerated: () => void;
}

type HoldMode = 'hold' | 'undo' | null;

/** The next action on the page. The button says how many people the file will hold, and a catch-up file says so. */
export const GeneratePanel: React.FC<GeneratePanelProps> = ({
  year,
  month,
  monthName,
  preflight,
  coverage,
  holdBackEnabled,
  loading,
  noPayrollWarnOnly,
  onShowHeld,
  onChanged,
  onGenerated,
}) => {
  const { t } = useTranslation();
  const labels = useWpsLabels();
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [mode, setMode] = useState<HoldMode>(null);
  const [resultNotes, setResultNotes] = useState<WpsConfirmWarning[]>([]);

  const finished = useCallback((notes: WpsConfirmWarning[]) => {
    setMode(null);
    setResultNotes(notes);
    onChanged();
  }, [onChanged]);
  const { warnings, error, busy, run, reset } = useWpsConfirm(finished);

  const remaining = preflight?.employeesRemaining ?? 0;
  const inAFile = (coverage?.inFileCount ?? 0) + (coverage?.submittedCount ?? 0);
  const isCatchUp = inAFile > 0 && remaining > 0;
  const noPayroll = preflight !== null && preflight.employeesTotal === 0;
  const people = coverage?.items ?? [];
  const noPayrollNames = people.filter(p => p.state === 'NoPayroll').map(p => p.employeeName);
  const heldNames = people.filter(p => p.state === 'Held').map(p => p.employeeName);
  // Still To file, so the next file holds them at full net. Marking Paid or releasing a hold can both lead here.
  const markedPaidNames = people.filter(p => p.state === 'ToFile' && p.markedPaid).map(p => p.employeeName);
  const showNoPayrollNames = noPayrollNames.length > 0 && !noPayrollWarnOnly;
  const showHeldNames = heldNames.length > 0 && preflight !== null && remaining === 0;
  const legacy = preflight?.hasLegacyFile === true;
  const paidAnotherWayHolds = people.some(p => p.holdReason === 'PaidAnotherWay');
  const canHoldEveryone = holdBackEnabled && !legacy && ((coverage?.toFileCount ?? 0) + (coverage?.noPayrollCount ?? 0)) > 0;

  const generate = async () => {
    setGenerating(true);
    setGenerateError(null);
    try {
      await generateWpsFile(year, month);
      onGenerated();
    } catch (e) {
      setGenerateError(getApiErrorMessage(e, t, t('payroll.wps.uae.generate.failed')));
    } finally {
      setGenerating(false);
    }
  };

  const openMode = (next: 'hold' | 'undo') => {
    reset();
    setResultNotes([]);
    setMode(next);
    void run(() => (next === 'hold' ? holdEveryone(year, month, false) : releaseEveryone(year, month, false)));
  };

  const buttonLabel = (() => {
    if (remaining > 0) {
      return isCatchUp
        ? t('payroll.wps.uae.generate.catchUpButton', { count: remaining })
        : t('payroll.wps.uae.generate.button', { count: remaining });
    }
    // Remaining counts payroll rows to file. It reads 0 without an answer, and with people who have no row or are held.
    if (!preflight || !coverage) {
      return loading ? t('payroll.wps.uae.generate.checking') : t('payroll.wps.uae.generate.checksFailed');
    }
    if (noPayrollNames.length > 0 || heldNames.length > 0) return t('payroll.wps.uae.generate.nobodyReady');
    return inAFile > 0 ? t('payroll.wps.uae.generate.everyoneInFile') : t('payroll.wps.uae.generate.nobodyToFile');
  })();

  return (
    <section className="rounded-lg border border-border bg-card p-4" data-testid="wps-generate-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-semibold text-foreground">{t('payroll.wps.uae.generate.title', { month: monthName })}</h3>
          {noPayroll && !showNoPayrollNames && (
            <p className="text-sm text-muted-foreground">
              {t('payroll.wps.uae.generate.noPayroll')} <PayrollTableLink />
            </p>
          )}
        </div>
        <Button
          onClick={generate}
          disabled={generating || !preflight?.canGenerate}
          loading={generating}
          iconName="Download"
          data-testid="generate-sif-btn"
        >
          {buttonLabel}
        </Button>
      </div>

      {showNoPayrollNames && (
        <p
          className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
          data-testid="wps-no-payroll-named"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" aria-hidden="true" />
          <span>
            {t('payroll.wps.uae.generate.noPayrollNamed', { month: monthName, names: labels.cappedNames(noPayrollNames) })}{' '}
            <PayrollTableLink />
          </span>
        </p>
      )}

      {showHeldNames && (
        <p
          className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
          data-testid="wps-held-named"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" aria-hidden="true" />
          <span>
            {t('payroll.wps.uae.generate.heldNamed', { names: labels.cappedNames(heldNames) })}{' '}
            <button
              type="button"
              className="font-medium text-primary hover:underline"
              onClick={onShowHeld}
              data-testid="wps-show-held"
            >
              {t('payroll.wps.uae.generate.showHeld')}
            </button>
          </span>
        </p>
      )}

      {markedPaidNames.length > 0 && (
        <p
          className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
          data-testid="wps-marked-paid-named"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" aria-hidden="true" />
          <span>
            {t('payroll.wps.uae.generate.markedPaidNamed', { names: labels.cappedNames(markedPaidNames) })}
            {holdBackEnabled && <> {t('payroll.wps.uae.generate.markedPaidHold')}</>}
          </span>
        </p>
      )}

      {isCatchUp && (
        <p
          className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
          data-testid="wps-second-file-warning"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" aria-hidden="true" />
          <span>{t('payroll.wps.uae.generate.secondFile')}</span>
        </p>
      )}

      {generateError && (
        <p className="mt-3 text-sm text-red-700 dark:text-red-300" role="alert" data-testid="wps-generate-error">
          {generateError}
        </p>
      )}

      {(canHoldEveryone || paidAnotherWayHolds) && mode === null && (
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm">
          {canHoldEveryone && (
            <button
              type="button"
              className="font-medium text-muted-foreground underline underline-offset-2 hover:text-foreground"
              onClick={() => openMode('hold')}
              data-testid="wps-hold-everyone"
            >
              {t('payroll.wps.uae.holdEveryone.action')}
            </button>
          )}
          {paidAnotherWayHolds && (
            <button
              type="button"
              className="font-medium text-muted-foreground underline underline-offset-2 hover:text-foreground"
              onClick={() => openMode('undo')}
              data-testid="wps-hold-everyone-undo"
            >
              {t('payroll.wps.uae.holdEveryone.undo')}
            </button>
          )}
        </div>
      )}

      {mode !== null && (
        <InlineConfirm
          warnings={warnings}
          error={error}
          busy={busy}
          confirmLabel={mode === 'hold' ? t('payroll.wps.uae.holdEveryone.confirm') : t('payroll.wps.uae.holdEveryone.confirmUndo')}
          canConfirm={warnings !== null}
          onConfirm={() => void run(() => (mode === 'hold' ? holdEveryone(year, month, true) : releaseEveryone(year, month, true)))}
          onCancel={() => {
            reset();
            setMode(null);
          }}
          testId="wps-hold-everyone-confirm"
        />
      )}

      {mode === null && resultNotes.length > 0 && (
        <div className="mt-3" data-testid="wps-hold-everyone-result">
          <WarningList warnings={resultNotes} />
        </div>
      )}
    </section>
  );
};
